<template>
  <div>
    <Navigation />
    <v-main>
      <v-container fluid>
        <!-- En-tête de la page -->
        <div class="page-header">
          <div class="header-content">
            <span class="material-symbols-outlined header-icon">admin_panel_settings</span>
            <div>
              <h1 class="page-title">Administration</h1>
              <p class="page-subtitle">Tableau de bord système et monitoring</p>
            </div>
          </div>
        </div>

        <v-row>
          <!-- User Activity Section -->
          <v-col cols="12" md="6">
            <v-card class="dashboard-card">
              <div class="card-header">
                <div class="header-content">
                  <span class="material-symbols-outlined">group</span>
                  <h2 class="card-title">Activité Utilisateurs</h2>
                </div>
              </div>
              <v-card-text>
                <v-row>
                  <v-col cols="6">
                    <div class="stat-card">
                      <span class="material-symbols-outlined stat-icon">person</span>
                      <div class="stat-content">
                        <div class="stat-value">{{ totalUsers || '...' }}</div>
                        <div class="stat-label">Total Utilisateurs</div>
                      </div>
                    </div>
                  </v-col>
                  <v-col cols="6">
                    <div class="stat-card">
                      <span class="material-symbols-outlined stat-icon active">radio_button_checked</span>
                      <div class="stat-content">
                        <div class="stat-value">{{ activeUsers || '...' }}</div>
                        <div class="stat-label">Utilisateurs Actifs</div>
                      </div>
                    </div>
                  </v-col>
                  <v-col cols="6">
                    <div class="stat-card">
                      <span class="material-symbols-outlined stat-icon">trending_up</span>
                      <div class="stat-content">
                        <div class="stat-value">{{ retentionRate ? retentionRate + '%' : '...' }}</div>
                        <div class="stat-label">Taux de Rétention</div>
                      </div>
                    </div>
                  </v-col>
                  <v-col cols="6">
                    <div class="stat-card">
                      <span class="material-symbols-outlined stat-icon">person_add</span>
                      <div class="stat-content">
                        <div class="stat-value">{{ newSignups }}</div>
                        <div class="stat-label">Nouvelles Inscriptions</div>
                      </div>
                    </div>
                  </v-col>
                </v-row>
              </v-card-text>
            </v-card>
          </v-col>

          <!-- App Performance Section -->
          <v-col cols="12" md="6">
            <v-card class="dashboard-card">
              <div class="card-header">
                <div class="header-content">
                  <span class="material-symbols-outlined">speed</span>
                  <h2 class="card-title">Métriques de Performance</h2>
                </div>
              </div>
              <v-card-text>
                <v-row>
                  <v-col cols="6">
                    <div class="stat-card">
                      <span class="material-symbols-outlined stat-icon">bolt</span>
                      <div class="stat-content">
                        <div class="stat-value">{{ apiResponseTime ? apiResponseTime + 'ms' : '...' }}</div>
                        <div class="stat-label">Temps de Réponse API</div>
                      </div>
                    </div>
                  </v-col>
                  <v-col cols="6">
                    <div class="stat-card">
                      <span class="material-symbols-outlined stat-icon">cloud_done</span>
                      <div class="stat-content">
                        <div class="stat-value">{{ uptimePercentage }}%</div>
                        <div class="stat-label">Disponibilité</div>
                      </div>
                    </div>
                  </v-col>
                </v-row>
              </v-card-text>
            </v-card>
          </v-col>

          <!-- Error Tracking Section -->
          <v-col cols="12">
            <v-card class="dashboard-card">
              <div class="card-header">
                <div class="header-content">
                  <span class="material-symbols-outlined">bug_report</span>
                  <h2 class="card-title">Suivi des Erreurs et Avertissements</h2>
                </div>
              </div>
              <v-card-text>
                <v-data-table
                    :headers="errorHeaders"
                    :items="notifications"
                    :items-per-page="itemsPerPage"
                    :page.sync="page"
                    :items-per-page-options="[5, 10, 25]"
                    :expanded.sync="expanded"
                    show-expand
                    class="modern-table"
                    hide-default-footer
                >
                  <template v-slot:expanded-item="{ headers, item }">
                    <td :colspan="headers.length" class="error-details-cell">
                      <div class="error-details-container">
                        <div class="error-detail-row">
                          <span class="material-symbols-outlined detail-icon">schedule</span>
                          <div>
                            <strong>Date de l'événement</strong>
                            <p>{{ formatDate(item.updated_at) }}</p>
                          </div>
                        </div>
                        <div class="error-detail-row">
                          <span class="material-symbols-outlined detail-icon">description</span>
                          <div class="error-description">
                            <strong>Description complète</strong>
                            <pre class="error-code">{{ item.full_description }}</pre>
                          </div>
                        </div>
                      </div>
                    </td>
                  </template>
                  <template v-slot:item="props">
                    <tr :class="getRowClass(props.item)">
                      <td class="expand-cell">
                        <v-btn icon small @click="toggleExpand(props.item)" class="expand-btn">
                                                    <span class="material-symbols-outlined">
                                                        {{ isExpanded(props.item) ? 'expand_less' : 'expand_more' }}
                                                    </span>
                        </v-btn>
                      </td>
                      <td>{{ props.item.short_description }}</td>
                      <td>
                                                <span :class="'type-badge type-' + props.item.type.toLowerCase()">
                                                    <span class="material-symbols-outlined">
                                                        {{ props.item.type === 'Error' ? 'error' : 'warning' }}
                                                    </span>
                                                    {{ props.item.type }}
                                                </span>
                      </td>
                      <td>{{ props.item.source }}</td>
                      <td>{{ formatDate(props.item.updated_at) }}</td>
                    </tr>
                  </template>
                </v-data-table>
                <div class="table-pagination">
                  <v-pagination
                      v-model="page"
                      :length="Math.ceil(notifications.length / itemsPerPage)"
                      :total-visible="7"
                      circle
                  ></v-pagination>
                </div>
              </v-card-text>
            </v-card>
          </v-col>
        </v-row>
      </v-container>
    </v-main>
  </div>
</template>

<script>
import { mapGetters, mapActions } from 'vuex';
import Vue from 'vue';

export default {
  data() {
    return {
      newSignups: 120,
      apiResponseTime: null,
      uptimePercentage: null,
      expanded: [],
      itemsPerPage: 5,
      serverMessage: "",
      page: 1,

      errorHeaders: [
        { text: 'Description', value: 'short_description' },
        { text: 'Type', value: 'type' },
        { text: 'Source', value: 'source' },
        { text: 'Date', value: 'updated_at' },
      ],
      notifications: [],
    };
  },
  computed: {
    ...mapGetters('admin', ['getTotalUsers', 'getActiveUsers', 'getRetentionRate']),
    totalUsers() {
      return this.getTotalUsers;
    },
    activeUsers() {
      return this.getActiveUsers;
    },
    retentionRate() {
      return this.getRetentionRate;
    },
  },
  mounted() {
    this.fetchApiResponseTime();
    this.fetchUptimePercentage();
    this.fetchNotifications();
  },
  methods: {
    ...mapActions('admin', ['fetchTotalUsers', 'fetchActiveUsers', 'fetchRetentionRate']),

    fetchApiResponseTime() {
      setTimeout(() => {
        const startTime = performance.now();

        Vue.axios.get('/api/ping')
            .then(response => {
              const endTime = performance.now();
              this.apiResponseTime = (endTime - startTime).toFixed(2);
              this.serverMessage = response.data.message;
            })
            .catch(error => {
              console.error('Error fetching ping:', error);
            });
      }, 3000);
    },
    formatDate(dateString) {
      const date = new Date(dateString);
      return date.toLocaleString('fr-FR', {
        timeZone: 'Europe/Paris',
        year: 'numeric',
        month: '2-digit',
        day: '2-digit',
        hour: '2-digit',
        minute: '2-digit',
      });
    },
    toggleExpand(item) {
      const index = this.expanded.indexOf(item);
      if (index > -1) {
        this.expanded.splice(index, 1);
      } else {
        this.expanded.push(item);
      }
    },
    isExpanded(item) {
      return this.expanded.includes(item);
    },
    getRowClass(item) {
      return item.type === 'Error' ? 'error-row' : '';
    },
    fetchUptimePercentage() {
      Vue.axios.get('/api/uptime')
          .then(response => {
            this.uptimePercentage = response.data.uptime;
          })
          .catch(error => {
            console.error('Error fetching uptime percentage:', error);
          });
    },
    fetchNotifications() {
      Vue.axios.get('/api/notifications')
          .then(response => {
            this.notifications = response.data.data;
          })
          .catch(error => {
            console.error('Error fetching notifications:', error);
          });
    },
  },
};
</script>

<style scoped>
/* Import des Material Symbols */
@import url('https://fonts.googleapis.com/css2?family=Material+Symbols+Outlined:opsz,wght,FILL,GRAD@20..48,100..700,0..1,-50..200');

/* En-tête de page */
.page-header {
  margin-bottom: 24px;
  padding: 24px;
  background: linear-gradient(135deg, #1e574b 0%, #2d7a6a 100%);
  border-radius: 12px;
  box-shadow: 0 4px 6px rgba(0, 0, 0, 0.1);
}

.page-header .header-content {
  display: flex;
  align-items: center;
  gap: 16px;
  color: white;
}

.page-header .header-icon {
  font-size: 48px;
  opacity: 0.9;
}

.page-title {
  margin: 0;
  font-size: 28px;
  font-weight: 600;
  color: white;
}

.page-subtitle {
  margin: 4px 0 0 0;
  font-size: 14px;
  opacity: 0.9;
  color: white;
}

/* Cards */
.dashboard-card {
  border-radius: 12px;
  box-shadow: 0 2px 8px rgba(0, 0, 0, 0.08);
  height: 100%;
  transition: all 0.3s ease;
}

.dashboard-card:hover {
  box-shadow: 0 4px 16px rgba(0, 0, 0, 0.12);
  transform: translateY(-2px);
}

.card-header {
  background: linear-gradient(135deg, #2d7a6a 0%, #3a9580 100%);
  padding: 20px;
  border-radius: 12px 12px 0 0;
}

.card-header .header-content {
  display: flex;
  align-items: center;
  gap: 12px;
}

.card-header .material-symbols-outlined {
  font-size: 28px;
  color: white;
}

.card-title {
  margin: 0;
  font-size: 18px;
  font-weight: 600;
  color: white;
}

/* Stat Cards */
.stat-card {
  background: linear-gradient(135deg, #f8fffe 0%, #e8f5f3 100%);
  border-radius: 12px;
  padding: 20px;
  display: flex;
  align-items: center;
  gap: 16px;
  transition: all 0.3s ease;
  border: 1px solid rgba(45, 122, 106, 0.1);
}

.stat-card:hover {
  transform: translateY(-2px);
  box-shadow: 0 4px 12px rgba(45, 122, 106, 0.15);
  border-color: rgba(45, 122, 106, 0.2);
}

.stat-icon {
  font-size: 40px;
  color: #2d7a6a;
  opacity: 0.8;
  flex-shrink: 0;
}

.stat-icon.active {
  color: #4caf50;
}

.stat-content {
  flex: 1;
}

.stat-value {
  font-size: 28px;
  font-weight: 700;
  color: #1e574b;
  line-height: 1;
  margin-bottom: 4px;
}

.stat-label {
  font-size: 13px;
  color: #5a8a7e;
  font-weight: 500;
}

/* Table moderne */
.modern-table {
  border-radius: 8px;
  overflow: hidden;
}

.modern-table >>> thead {
  background: linear-gradient(135deg, #2d7a6a 0%, #3a9580 100%);
}

.modern-table >>> thead th {
  color: white !important;
  font-weight: 600 !important;
  font-size: 14px !important;
  padding: 16px !important;
  border: none !important;
}

.modern-table >>> tbody tr {
  transition: all 0.2s ease;
}

.modern-table >>> tbody tr:hover {
  background-color: #f0f9f7 !important;
}

.modern-table >>> tbody td {
  padding: 14px 16px !important;
  border-bottom: 1px solid #e8f5f3 !important;
}

.error-row {
  background-color: #ffebee !important;
}

.error-row:hover {
  background-color: #ffcdd2 !important;
}

/* Type Badges */
.type-badge {
  display: inline-flex;
  align-items: center;
  gap: 6px;
  padding: 6px 12px;
  border-radius: 20px;
  font-size: 13px;
  font-weight: 600;
}

.type-badge .material-symbols-outlined {
  font-size: 18px;
}

.type-error {
  background-color: #ffebee;
  color: #c62828;
}

.type-warning {
  background-color: #fff3e0;
  color: #ef6c00;
}

/* Expand Button */
.expand-cell {
  width: 50px;
}

.expand-btn {
  color: #2d7a6a !important;
}

.expand-btn .material-symbols-outlined {
  font-size: 24px;
  transition: transform 0.3s ease;
}

/* Error Details */
.error-details-cell {
  background-color: #f8fffe !important;
  padding: 0 !important;
}

.error-details-container {
  padding: 24px;
  border-top: 2px solid #2d7a6a;
}

.error-detail-row {
  display: flex;
  gap: 16px;
  margin-bottom: 20px;
}

.error-detail-row:last-child {
  margin-bottom: 0;
}

.detail-icon {
  font-size: 24px;
  color: #2d7a6a;
  flex-shrink: 0;
  margin-top: 4px;
}

.error-detail-row strong {
  display: block;
  color: #1e574b;
  font-size: 14px;
  font-weight: 600;
  margin-bottom: 8px;
}

.error-detail-row p {
  margin: 0;
  color: #5a8a7e;
  font-size: 14px;
}

.error-description {
  flex: 1;
}

.error-code {
  background-color: #263238;
  color: #aed581;
  padding: 16px;
  border-radius: 8px;
  font-family: 'Courier New', monospace;
  font-size: 13px;
  line-height: 1.6;
  margin: 0;
  white-space: pre-wrap;
  word-wrap: break-word;
  max-height: 400px;
  overflow-y: auto;
  border: 1px solid #37474f;
}

.error-code::-webkit-scrollbar {
  width: 8px;
}

.error-code::-webkit-scrollbar-track {
  background: #1e2a2f;
  border-radius: 4px;
}

.error-code::-webkit-scrollbar-thumb {
  background: #546e7a;
  border-radius: 4px;
}

.error-code::-webkit-scrollbar-thumb:hover {
  background: #607d8b;
}

/* Pagination */
.table-pagination {
  display: flex;
  justify-content: center;
  margin-top: 24px;
}

.table-pagination >>> .v-pagination__item {
  background-color: #e8f5f3;
  color: #2d7a6a;
  font-weight: 600;
}

.table-pagination >>> .v-pagination__item--active {
  background-color: #2d7a6a !important;
  color: white !important;
}

.table-pagination >>> .v-pagination__navigation {
  background-color: #e8f5f3;
  color: #2d7a6a;
}

.table-pagination >>> .v-pagination__navigation:hover,
.table-pagination >>> .v-pagination__item:hover {
  background-color: #d0ebe6;
}

/* Responsive */
@media (max-width: 960px) {
  .page-header {
    padding: 20px;
  }

  .page-title {
    font-size: 24px;
  }

  .stat-value {
    font-size: 24px;
  }

  .stat-card {
    padding: 16px;
  }
}

@media (max-width: 600px) {
  .page-header .header-content {
    flex-direction: column;
    text-align: center;
  }

  .stat-card {
    flex-direction: column;
    text-align: center;
  }

  .stat-icon {
    font-size: 36px;
  }

  .error-detail-row {
    flex-direction: column;
    gap: 8px;
  }
}
</style>